import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import QuotePopup from '@/pages/home/components/QuotePopup';

interface FAQItem {
  q: string;
  a: string;
}

interface FAQCategory {
  title: string;
  icon: string;
  items: FAQItem[];
}

const faqData: FAQCategory[] = [
  {
    title: 'Ordering & Pricing',
    icon: 'ri-price-tag-3-line',
    items: [
      { q: 'What is your minimum order quantity?', a: 'Our standard minimum is 25 metric tons per product. For mixed container orders (multiple products in one container), we can accommodate smaller quantities per product. Contact us to discuss your specific requirements.' },
      { q: 'How do I get a price quote?', a: 'You can request a quote through our website form, WhatsApp (+86 17671262710), or email (support@steelink.com). Please provide: product name, quantity, destination port, and required delivery date. We typically respond within 2 hours during business hours.' },
      { q: 'Are your prices FOB or CIF?', a: 'We primarily quote FOB (Free On Board) — meaning the price covers getting your steel to the Chinese port, and you arrange the shipping from there. We can also quote CIF (Cost, Insurance & Freight) which includes the sea freight and insurance to your port, upon request.' },
      { q: 'Do prices include export documentation?', a: 'Yes. Our price includes all export documents: commercial invoice, packing list, Bill of Lading (your proof of shipment), Certificate of Origin (proves where the steel was made), and mill quality certificate. Additional independent inspections (SGS, Bureau Veritas, TUV) are available at extra cost.' },
      { q: 'Can I negotiate prices for large orders?', a: 'Absolutely. For orders above 100 metric tons, we offer volume discounts. For regular buyers with monthly orders, we can set up a long-term pricing agreement. Contact our sales team to discuss.' },
    ],
  },
  {
    title: 'Products & Quality',
    icon: 'ri-shield-check-line',
    items: [
      { q: 'What steel grades do you supply?', a: 'We supply a wide range including Chinese standards (Q235B, Q345B), American standards (ASTM A36, A53, A106, A500), European standards (EN 10025, EN 10219), Japanese standards (JIS G3101, G3141), and API 5L for oil & gas pipelines. Custom grades available on request.' },
      { q: 'Do you provide quality certificates from the mill?', a: 'Yes. All products come with original quality certificates from the producing mill. These include the chemical composition, strength test results, and batch numbers so you can trace exactly where your steel came from.' },
      { q: 'Can I request independent third-party inspection?', a: 'Yes. We work with SGS, Bureau Veritas, TUV, and Intertek — all internationally recognised inspection companies. They visit the mill before shipment and confirm the steel matches your order. Inspection costs are typically paid by the buyer but can be included in the price for large orders.' },
      { q: 'What surface finishes are available?', a: 'Depending on the product: Black (natural mill finish), Pickled & Oiled (cleaned and rust-protected), Hot-Dip Galvanized (zinc-coated for corrosion resistance), Pre-Painted (colour-coated), and Shot Blasted (surface cleaned for painting). Specify your requirement when requesting a quote.' },
      { q: 'Can you source custom sizes or grades not listed on your website?', a: 'Yes. Our website shows our most popular products, but we can source virtually any steel product from Chinese mills. Contact us with your specifications and we will find the right mill partner.' },
    ],
  },
  {
    title: 'Shipping & Logistics',
    icon: 'ri-ship-line',
    items: [
      { q: 'What are your shipping lead times?', a: 'Production time: 7–15 working days after deposit. Shipping time varies by destination: Southeast Asia 7–15 days, Middle East 15–25 days, Africa 20–35 days, South America 25–45 days, Europe 25–35 days.' },
      { q: 'Which ports do you ship from?', a: 'We ship from all major Chinese ports: Tianjin (North China), Qingdao (East China), Shanghai (East China), Ningbo (East China), Guangzhou/Nansha (South China), and Shenzhen (South China). We pick the most cost-effective port based on your destination.' },
      { q: 'Do you handle customs clearance?', a: 'We handle all Chinese export customs on our end. Import customs at your destination is your responsibility. We provide all the documents you need to make that process smooth.' },
      { q: 'Can you arrange inland delivery in China?', a: 'Yes. We can arrange trucking from the mill to the port. This is typically included in our price for standard mill locations. For remote mills, a small extra charge may apply.' },
      { q: 'What happens if goods are damaged during shipping?', a: 'All shipments are covered by marine cargo insurance. We help buyers file insurance claims for any damage that happens during transit. We also recommend buyers take out their own insurance for extra protection.' },
    ],
  },
  {
    title: 'Payment & Terms',
    icon: 'ri-bank-card-line',
    items: [
      { q: 'What payment methods do you accept?', a: 'We accept: Bank Transfer (T/T) — the most common and straightforward method; Letter of Credit (L/C) — a bank-guaranteed payment method, available for orders above $50,000; and Western Union for small orders. Bank transfer is our preferred method.' },
      { q: 'What are your standard payment terms?', a: 'Standard terms: 30% deposit when you confirm the order, 70% balance before we ship (once you see the copy of the shipping document). For established customers with a good track record, we can offer 70% against the original shipping document.' },
      { q: 'Can I get credit terms?', a: 'Payment on credit (net 30/60 days) is available for established customers who have completed at least 3 successful orders with us. Please discuss with our sales team.' },
      { q: 'Is my payment secure?', a: 'Yes. We use verified bank accounts and provide official bank details on company letterhead. Always confirm payment details directly with our team before sending any funds. We will never change bank details by email without a phone call to confirm.' },
    ],
  },
  {
    title: 'After-Sales & Support',
    icon: 'ri-customer-service-2-line',
    items: [
      { q: 'What if I receive the wrong product?', a: 'In the rare event of a product discrepancy, contact us immediately with photos and the quality certificate. We will investigate with the mill and arrange a replacement or compensation. Our quality control process is designed to prevent this from happening.' },
      { q: 'Do you offer after-sales technical support?', a: 'Yes. Our technical team can help with product selection, understanding specifications, and application guidance. We also advise on how to store, handle, and process steel products correctly.' },
      { q: 'Can I track my shipment?', a: 'Yes. Once your goods are shipped, we give you the shipping document number and vessel details. You can track your shipment on the shipping line\'s website. We also send you proactive updates along the way.' },
      { q: 'How do I become a regular buyer or distributor?', a: 'We welcome long-term partnerships. After your first successful order, contact our sales team to discuss distributor pricing, exclusive territory arrangements, and priority production scheduling.' },
    ],
  },
];

export default function FAQPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState('');

  const toggleItem = (key: string) => {
    setOpenItems((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filteredData = search.trim()
    ? faqData.map((cat) => ({
        ...cat,
        items: cat.items.filter(
          (item) =>
            item.q.toLowerCase().includes(search.toLowerCase()) ||
            item.a.toLowerCase().includes(search.toLowerCase())
        ),
      })).filter((cat) => cat.items.length > 0)
    : faqData;

  return (
    <div className="min-h-screen bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />
      {quoteOpen && <QuotePopup onClose={() => setQuoteOpen(false)} />}

      {/* Hero */}
      <section className="pt-32 pb-16 bg-gray-900">
        <div className="max-w-[1420px] mx-auto px-6 text-center">
          <span className="inline-block px-3 py-1 bg-yellow-400 text-gray-900 text-xs font-black rounded mb-4 tracking-widest uppercase">Help Center</span>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Frequently Asked Questions</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Everything you need to know about ordering steel from China with Steelinkglobal.
          </p>
          {/* Search */}
          <div className="max-w-lg mx-auto relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions..."
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-white text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16">
        <div className="max-w-[1420px] mx-auto px-6">
          {!search.trim() && (
            <div className="flex flex-wrap gap-2 mb-10 justify-center">
              {faqData.map((cat, i) => (
                <button
                  key={cat.title}
                  onClick={() => setActiveCategory(i)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === i
                      ? 'bg-yellow-400 text-gray-900'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <i className={cat.icon} />
                  {cat.title}
                </button>
              ))}
            </div>
          )}

          <div className="max-w-3xl mx-auto">
            {(search.trim() ? filteredData : [faqData[activeCategory]]).map((cat) => (
              <div key={cat.title} className="mb-10">
                {search.trim() && (
                  <h2 className="flex items-center gap-2 text-lg font-black text-gray-900 mb-4">
                    <i className={`${cat.icon} text-yellow-500`} />
                    {cat.title}
                  </h2>
                )}
                <div className="space-y-3">
                  {cat.items.map((item, idx) => {
                    const key = `${cat.title}-${idx}`;
                    const isOpen = openItems[key];
                    return (
                      <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleItem(key)}
                          className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 text-sm pr-4">{item.q}</span>
                          <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                            <i className={`${isOpen ? 'ri-subtract-line' : 'ri-add-line'} text-yellow-500`} />
                          </div>
                        </button>
                        {isOpen && (
                          <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3">
                            {item.a}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {search.trim() && filteredData.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mx-auto mb-4">
                  <i className="ri-search-line text-2xl text-gray-400" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-500 text-sm">Try different keywords or contact us directly.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-[1420px] mx-auto px-6 text-center">
          <h2 className="text-2xl font-black text-gray-900 mb-3">Still Have Questions?</h2>
          <p className="text-gray-500 mb-8 max-w-lg mx-auto">Our team is available Monday–Friday 9AM–6PM CST, and on WhatsApp 24/7 for urgent inquiries.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a href="https://wa.me/8617671262710" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-bold rounded-md text-sm hover:bg-green-600 transition-colors whitespace-nowrap">
              <i className="ri-whatsapp-line" /> Chat on WhatsApp
            </a>
            <a href="/contact" className="flex items-center gap-2 px-6 py-3 bg-yellow-400 text-gray-900 font-bold rounded-md text-sm hover:bg-yellow-500 transition-colors whitespace-nowrap">
              <i className="ri-mail-line" /> Send Us a Message
            </a>
            <button onClick={() => setQuoteOpen(true)} className="flex items-center gap-2 px-6 py-3 bg-gray-900 text-white font-bold rounded-md text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-list-3-line" /> Request a Quote
            </button>
          </div>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
